import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../components';
import css from './EditPieceStatusModal.css';

// Base status definitions for UI display
const STATUS_CONFIG = {
  available: { label: 'Dostępny', badgeClass: css.badgeAvailable },
  service: { label: 'Serwis', badgeClass: css.badgeService },
  damaged: { label: 'Zniszczone', badgeClass: css.badgeDamaged },
  sold: { label: 'Sprzedane 2nd life', badgeClass: css.badgeSold },
  transfer: { label: 'Transfer', badgeClass: css.badgeTransfer },
  rent: { label: 'Wypożyczony', badgeClass: css.badgeRented },
  'internal-use': { label: 'Wypożyczenie wewnętrzne', badgeClass: css.badgeReserved },
  stolen: { label: 'Kradzież', badgeClass: css.badgeStolen },
};

// Helper function to format store name: strips "Decathlon" and prepends dkCode if available (e.g. "272 - Zima")
const formatStoreName = (providerAttrs = {}) => {
  const rawName = providerAttrs.secondaryName || providerAttrs.name || 'Sklep';
  const cleanedName = rawName.replace(/^Decathlon\s*/i, '').trim();
  const dkCode = providerAttrs.dkCode;

  if (dkCode) {
    return `${dkCode} - ${cleanedName || rawName}`;
  }
  return cleanedName || rawName;
};

// Returns allowed manual target status options based strictly on CSV matrix and user roles
export const getAvailableTargetStatuses = (currentState, userPermissions = {}) => {
  const { isAdmin = false, isOwner = false, isEmployee = false } = userPermissions;
  const canEmployeeOrAbove = isEmployee || isOwner || isAdmin;

  switch (currentState) {
    case 'available':
      if (!canEmployeeOrAbove) return [];
      return [
        { value: 'service', label: 'Serwis', requiresDetails: true },
        { value: 'damaged', label: 'Zniszczone', requiresDetails: true },
        { value: 'sold', label: 'Sprzedane 2nd life', requiresDetails: false },
        { value: 'transfer', label: 'Transfer do innego sklepu', requiresDetails: true },
      ];

    case 'service':
      if (!canEmployeeOrAbove) return [];
      return [
        { value: 'available', label: 'Dostępny (Zakończ serwis)', requiresDetails: false },
        { value: 'damaged', label: 'Zniszczone', requiresDetails: true },
        { value: 'sold', label: 'Sprzedane 2nd life', requiresDetails: false },
        { value: 'transfer', label: 'Transfer do innego sklepu', requiresDetails: true },
      ];

    case 'transfer':
      if (!canEmployeeOrAbove) return [];
      return [
        { value: 'available', label: 'Dostępny (Przyjmij na sklep)', requiresDetails: false },
        { value: 'service', label: 'Serwis', requiresDetails: true },
        { value: 'damaged', label: 'Zniszczone', requiresDetails: true },
      ];

    case 'damaged':
    case 'sold':
    case 'stolen':
      return isAdmin ? [{ value: 'available', label: 'Dostępny (Przywróć - Admin)', requiresDetails: false }] : [];

    default:
      return [];
  }
};

export function EditPieceStatusModal({ open, onOpenChange, piece, sdk, userPermissions, currentProvider }) {
  const queryClient = useQueryClient();

  const [step, setStep] = useState('select'); // 'select' | 'details'
  const [selectedTarget, setSelectedTarget] = useState(null);

  // Form fields
  const [serviceReason, setServiceReason] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [damageReason, setDamageReason] = useState('');
  const [targetProviderId, setTargetProviderId] = useState('');
  const [apiError, setApiError] = useState(null);

  const currentState = piece?.attributes?.state || 'available';
  const pieceCode = piece?.attributes?.code || 'Brak numeru';
  const currentConfig = STATUS_CONFIG[currentState] || STATUS_CONFIG.available;
  const availableTargets = getAvailableTargetStatuses(currentState, userPermissions);

  const currentProviderId = currentProvider?.id?.uuid || currentProvider?.id;
  const currentCountryId =
    currentProvider?.attributes?.countryId ||
    currentProvider?.attributes?.country ||
    currentProvider?.attributes?.address?.country;

  // Query target stores (isDecathlon: true, countryId matching current store)
  const { data: providersResponse, isLoading: isProvidersLoading } = useQuery({
    queryKey: ['targetProviders', currentCountryId],
    queryFn: () =>
      sdk.newSdk.providers.query({
        isDecathlon: true,
        countryId: currentCountryId,
      }),
    enabled: open && selectedTarget === 'transfer' && !!currentCountryId,
    staleTime: 1000 * 60 * 10,
  });

  // Filter out current store & format store names (including dkCode)
  const availableTargetStores = useMemo(() => {
    const rawList = providersResponse?.data?.data || providersResponse?.data || providersResponse || [];
    if (!Array.isArray(rawList)) return [];

    return rawList
      .filter((p) => (p.id?.uuid || p.id) !== currentProviderId)
      .map((p) => ({
        id: p.id?.uuid || p.id,
        cleanName: formatStoreName(p.attributes || {}),
        rawName: p.attributes?.name,
      }));
  }, [providersResponse, currentProviderId]);

  // Reset modal state whenever opening/closing
  useEffect(() => {
    if (open) {
      setStep('select');
      setSelectedTarget(null);
      setServiceReason('');
      setReturnDate('');
      setDamageReason('');
      setTargetProviderId('');
      setApiError(null);
    }
  }, [open, piece]);

  // Mutation handler supporting API actions
  const { mutate: handleUpdateStatus, isLoading: isSubmitting } = useMutation({
    mutationFn: (targetState) => {
      setApiError(null);
      const pieceId = piece?.id?.uuid || piece?.id;
      const piecesApi = sdk?.pieces || sdk?.newSdk?.pieces;

      if (!piecesApi) {
        return Promise.reject(new Error('Brak dostępnego SDK dla sztuk (sdk.pieces).'));
      }

      // 1. START TRANSFER (Wysłanie na transfer)
      if (targetState === 'transfer') {
        const transferPayload = { providerId: targetProviderId };

        if (typeof piecesApi.startTransfer === 'function') {
          return piecesApi.startTransfer({ id: pieceId, ...transferPayload });
        }
        return piecesApi.update({
          id: pieceId,
          startTransfer: transferPayload,
        });
      }

      // 2. COMPLETE TRANSFER (Przyjęcie transferu na sklep)
      if (currentState === 'transfer' && targetState === 'available') {
        if (typeof piecesApi.completeTransfer === 'function') {
          return piecesApi.completeTransfer({ id: pieceId });
        }
        return piecesApi.update({
          id: pieceId,
          completeTransfer: {},
        });
      }

      // 3. START SERVICE
      if (targetState === 'service') {
        const startServiceData = {
          reason: serviceReason.trim(),
          endDate: returnDate ? returnDate : null,
        };

        if (typeof piecesApi.startService === 'function') {
          return piecesApi.startService({ id: pieceId, ...startServiceData });
        }
        return piecesApi.update({
          id: pieceId,
          startService: startServiceData,
        });
      }

      // 4. COMPLETE SERVICE
      if (currentState === 'service' && targetState === 'available') {
        if (typeof piecesApi.completeService === 'function') {
          return piecesApi.completeService({ id: pieceId });
        }
        return piecesApi.update({
          id: pieceId,
          completeService: {},
        });
      }

      // 5. RECOVER (Admin action)
      const isRecoverState = currentState === 'damaged' || currentState === 'sold' || currentState === 'stolen';
      if (targetState === 'available' && isRecoverState) {
        if (typeof piecesApi.recover === 'function') {
          return piecesApi.recover({ id: pieceId });
        }
        return piecesApi.update({ id: pieceId, recover: {} });
      }

      // 6. SELL ACTION
      if (targetState === 'sold') {
        if (typeof piecesApi.sell === 'function') {
          return piecesApi.sell({ id: pieceId });
        }
        return piecesApi.update({ id: pieceId, sell: {} });
      }

      // 7. DAMAGE ACTION
      if (targetState === 'damaged') {
        if (typeof piecesApi.damage === 'function') {
          return piecesApi.damage({ id: pieceId, reason: damageReason.trim() });
        }
        return piecesApi.update({
          id: pieceId,
          damage: { reason: damageReason.trim() },
        });
      }

      // 8. DEFAULT STATE CHANGE
      return piecesApi.update({
        id: pieceId,
        state: targetState,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['pieces']);
      queryClient.invalidateQueries(['ownListings']);
      onOpenChange(false);
    },
    onError: (error) => {
      console.error('Error while updating piece status:', error);
      const rawError = error?.response?.data || error?.data || error;
      let errorMessage = '';

      if (typeof rawError === 'string') {
        errorMessage = rawError.trim();
      } else if (rawError?.errors?.[0]?.detail) {
        errorMessage = rawError.errors[0].detail;
      } else if (rawError?.message) {
        errorMessage = rawError.message;
      }

      setApiError(errorMessage || 'Nie udało się zmienić statusu sztuki.');
    },
  });

  const handleSelectTarget = (targetConfig) => {
    setSelectedTarget(targetConfig.value);
    setApiError(null);

    if (targetConfig.requiresDetails) {
      setStep('details');
    } else {
      handleUpdateStatus(targetConfig.value);
    }
  };

  const handleDetailsSubmit = (e) => {
    e.preventDefault();

    if (selectedTarget === 'damaged' && !damageReason.trim()) {
      setApiError('Proszę podać powód zniszczenia.');
      return;
    }
    if (selectedTarget === 'service' && !serviceReason.trim()) {
      setApiError('Proszę podać powód przekazania na serwis.');
      return;
    }
    if (selectedTarget === 'transfer' && !targetProviderId) {
      setApiError('Proszę wybrać sklep docelowy.');
      return;
    }

    handleUpdateStatus(selectedTarget);
  };

  return (
    <Modal
      isOpen={open}
      onClose={() => !isSubmitting && onOpenChange(false)}
      id="EditPieceStatusModal"
      title="Zmień status sztuki"
      onManageDisableScrolling={() => {}}
      usePortal
    >
      <div className={css.modalContainer}>
        {/* Modal Header & Current Status Badge */}
        <div className={css.headerMeta}>
          <h2 className={css.modalTitle}>Zmień status</h2>
          <div className={css.pieceCode}>{pieceCode}</div>

          <div className={css.currentStatusRow}>
            <span className={css.statusLabel}>Aktualny status:</span>
            <span className={`${css.statusBadge} ${currentConfig.badgeClass}`}>
              {currentConfig.label}
            </span>
          </div>
        </div>

        {/* --- STEP 1: TILE SELECTION --- */}
        {step === 'select' && (
          <div className={css.stepContent}>
            <div className={css.sectionLabel}>Zmień na</div>

            <div className={css.tilesList}>
              {availableTargets.map((target) => (
                <button
                  key={target.value}
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => handleSelectTarget(target)}
                  className={css.statusTile}
                >
                  <span className={css.tileLabel}>{target.label}</span>
                </button>
              ))}
            </div>

            {apiError && (
              <div className={css.errorBanner}>
                <span className={css.errorIcon}>⚠️</span>
                <span>{apiError}</span>
              </div>
            )}

            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => onOpenChange(false)}
              className={css.cancelTileBtn}
            >
              Anuluj
            </button>
          </div>
        )}

        {/* --- STEP 2: DETAILS STEP --- */}
        {step === 'details' && (
          <form onSubmit={handleDetailsSubmit} className={css.stepContent}>
            {/* SERVICE DETAILS */}
            {selectedTarget === 'service' && (
              <>
                <div className={css.inputWrapper}>
                  <label htmlFor="serviceReasonInput" className={css.label}>
                    Powód przekazania na serwis *
                  </label>
                  <input
                    id="serviceReasonInput"
                    type="text"
                    value={serviceReason}
                    onChange={(e) => setServiceReason(e.target.value)}
                    placeholder="np. Przegląd okresowy, naprawa hamulców"
                    disabled={isSubmitting}
                    className={css.textInput}
                    autoFocus
                    required
                  />
                </div>
                <div className={css.inputWrapper}>
                  <label htmlFor="returnDateInput" className={css.label}>
                    Planowana data powrotu z serwisu (opcjonalnie)
                  </label>
                  <input
                    id="returnDateInput"
                    type="date"
                    value={returnDate}
                    onChange={(e) => setReturnDate(e.target.value)}
                    disabled={isSubmitting}
                    className={css.textInput}
                  />
                </div>
              </>
            )}

            {/* DAMAGED DETAILS */}
            {selectedTarget === 'damaged' && (
              <>
                <div className={css.inputWrapper}>
                  <label htmlFor="damageReasonInput" className={css.label}>
                    Powód zniszczenia *
                  </label>
                  <input
                    id="damageReasonInput"
                    type="text"
                    value={damageReason}
                    onChange={(e) => setDamageReason(e.target.value)}
                    placeholder="np. Pęknięta rama po wypadku"
                    disabled={isSubmitting}
                    className={css.textInput}
                    autoFocus
                    required
                  />
                </div>
                <div className={css.warningNotice}>
                  <span className={css.warningIcon}>⚠️</span>
                  <span>
                    Proces oznaczania jako zniszczone jest nieodwracalny (tylko Admin może go cofnąć).
                  </span>
                </div>
              </>
            )}

            {/* TRANSFER DETAILS */}
            {selectedTarget === 'transfer' && (
              <div className={css.inputWrapper}>
                <label htmlFor="targetStoreSelect" className={css.label}>
                  Wybierz sklep docelowy *
                </label>
                {isProvidersLoading ? (
                  <div className={css.helperText}>Ładowanie listy sklepów...</div>
                ) : availableTargetStores.length > 0 ? (
                  <select
                    id="targetStoreSelect"
                    value={targetProviderId}
                    onChange={(e) => setTargetProviderId(e.target.value)}
                    className={css.selectInput}
                    disabled={isSubmitting}
                    autoFocus
                    required
                  >
                    <option value="">-- Wybierz sklep z listy --</option>
                    {availableTargetStores.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.cleanName}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className={css.errorBanner}>
                    Brak innych dostępnych sklepów do wykonania transferu.
                  </div>
                )}
              </div>
            )}

            {apiError && (
              <div className={css.errorBanner}>
                <span className={css.errorIcon}>⚠️</span>
                <span>{apiError}</span>
              </div>
            )}

            <div className={css.modalActions}>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => setStep('select')}
                className={css.secondaryBtn}
              >
                Wróć
              </button>
              <button
                type="submit"
                disabled={isSubmitting || (selectedTarget === 'transfer' && !targetProviderId)}
                className={css.primaryBtn}
              >
                {isSubmitting ? 'Zapisywanie...' : 'Zatwierdź zmianę'}
              </button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}